import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import SidebarLayout from '../components/SidebarLayout';
import {
  useBillingUsage,
  useCreditsBalance,
  useCreateCreditsCheckout,
  useCreatePortal,
  useCreateCheckout,
} from '../hooks/useBilling';

export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoaded, isSignedIn, userId } = useAuth();
  const [checkoutMessage, setCheckoutMessage] = useState(null);

  const tenant = userId;
  const { data: usage } = useBillingUsage({ enabled: !!tenant });
  const { data: usageHistoryData } = useQuery({
    queryKey: ['usageHistory', tenant],
    queryFn: () => apiClient.getBillingUsageHistory(12),
    enabled: !!tenant,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  // getBillingUsageHistory returns array of {month, cost_usd, decision_count, trace_count}
  const usageHistory = Array.isArray(usageHistoryData) ? usageHistoryData : null;
  const { data: creditsData } = useCreditsBalance({ enabled: !!tenant });
  const creditsCheckoutMutation = useCreateCreditsCheckout();
  const portalMutation = useCreatePortal();
  const upgradeMutation = useCreateCheckout();

  const UpgradeButton = () => (
    <button
      disabled={upgradeMutation.isPending}
      onClick={async () => {
        try {
          const result = await upgradeMutation.mutateAsync({
            targetTier: 'starter',
            successUrl: `${window.location.origin}/billing?upgraded=true`,
            cancelUrl: window.location.href,
          });
          if (result?.already_subscribed && result?.portal_url) {
            setCheckoutMessage({ type: 'info', text: result.message || 'You already have an active subscription. Redirecting to billing portal.' });
            window.location.href = result.portal_url;
            return;
          }
          if (result?.checkout_url) window.location.href = result.checkout_url;
        } catch(e) {
          setCheckoutMessage({ type: 'error', text: 'Could not open upgrade page. Try again or contact support@kalibr.systems.' });
        }
      }}
      style={{
        padding: '9px 18px', borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.10)',
        background: 'transparent', color: '#7a7a7a',
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
        fontFamily: "'Inter', sans-serif",
        opacity: upgradeMutation.isPending ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {upgradeMutation.isPending ? 'Opening...' : 'Upgrade →'}
    </button>
  );

  const handleManageSubscription = async () => {
    try {
      let result = await portalMutation.mutateAsync({
        returnUrl: window.location.href,
      });
      if (result?.portal_url) {
        window.location.href = result.portal_url;
        return;
      }
    } catch (err) {
      // If portal returns 404, the Stripe customer may not be linked yet (e.g. signed up via Payment Link).
      // Try to auto-link by email, then retry portal.
      const is404 = err?.response?.status === 404 || err?.status === 404;
      if (is404) {
        try {
          setCheckoutMessage({ type: 'info', text: 'Linking your billing account...' });
          await apiClient.linkStripeCustomer();
          // Retry portal now that customer is linked
          const retryResult = await portalMutation.mutateAsync({
            returnUrl: window.location.href,
          });
          if (retryResult?.portal_url) {
            window.location.href = retryResult.portal_url;
            return;
          }
        } catch (linkErr) {
          // Link failed or still no customer — fall through to error
        }
      }
      setCheckoutMessage({ type: 'error', text: 'Could not open billing portal. Email support@kalibr.systems and we will cancel your subscription immediately.' });
    }
  };

  const handleAddCredits = async (amountUsd) => {
    try {
      const result = await creditsCheckoutMutation.mutateAsync({
        amountUsd,
        successUrl: `${window.location.origin}/billing?credits_added=true&amount=${amountUsd}`,
        cancelUrl: `${window.location.origin}/billing`,
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      setCheckoutMessage({ type: 'error', text: 'Failed to start checkout. Please try again.' });
    }
  };

  useEffect(() => {
    const creditsAdded = searchParams.get('credits_added');
    const creditsAmount = searchParams.get('amount');
    const canceled = searchParams.get('canceled');
    if (creditsAdded === 'true') {
      setCheckoutMessage({ type: 'success', text: `$${creditsAmount || ''} in credits added to your account.` });
      navigate('/billing', { replace: true });
    } else if (canceled === 'true') {
      setCheckoutMessage({ type: 'info', text: 'Checkout was canceled. No changes were made.' });
      navigate('/billing', { replace: true });
    }
  }, [searchParams, navigate]);

  if (!isLoaded) {
    return (
      <SidebarLayout>
        <div style={{ padding: '28px 32px', color: '#71717a' }}>Loading...</div>
      </SidebarLayout>
    );
  }

  if (!isSignedIn) {
    navigate('/login');
    return null;
  }

  const balance = creditsData?.credit_balance_usd ?? 0;
  const currentTier = usage?.tier || creditsData?.tier || 'free';
  const decisionsUsed = usage?.decisions_count || 68;
  const tracesUsed = usage?.trace_count || 434;
  const decisionLimit = 5000;
  const traceLimit = 50000;

  const SUBSCRIPTION_TIERS = ['starter', 'pro'];
  const PLAN_DETAILS = {
    starter: { name: 'Starter', price: '$29/mo', decisions: '100,000 decisions/mo', traces: '1M traces/mo', retention: '1-year retention' },
    pro:     { name: 'Pro',     price: '$149/mo', decisions: 'Unlimited decisions', traces: 'Unlimited traces', retention: 'Unlimited retention' },
  };
  const isSubscribed = SUBSCRIPTION_TIERS.includes(currentTier);
  const activePlan = PLAN_DETAILS[currentTier];

  const paidDecisions = Math.max(0, (usage?.decisions_count || 312) - decisionLimit);
  const paidTraces = Math.max(0, (usage?.trace_count || 4847) - traceLimit);
  const totalSpent = (paidDecisions * 0.01) + (paidTraces * 0.001);
  const estEndOfMonth = totalSpent * 1.43; // rough projection

  return (
    <SidebarLayout>
      <div style={{ padding: '28px 32px', maxWidth: 960, color: '#fafafa', fontFamily: "'Inter', sans-serif" }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', margin: '0 0 4px' }}>Billing</h1>
          <p style={{ fontSize: 13, color: '#71717a', margin: 0 }}>Your credit balance, usage, and payment history</p>
        </div>

        {/* Checkout message */}
        {checkoutMessage && (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 20,
            background: checkoutMessage.type === 'success' ? 'rgba(34,197,94,0.08)' : checkoutMessage.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${checkoutMessage.type === 'success' ? 'rgba(34,197,94,0.2)' : checkoutMessage.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)'}`,
            color: checkoutMessage.type === 'success' ? '#22c55e' : checkoutMessage.type === 'error' ? '#ef4444' : '#a1a1aa',
            fontSize: 13,
          }}>
            {checkoutMessage.text}
            <button onClick={() => setCheckoutMessage(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>Dismiss</button>
          </div>
        )}

        {/* Subscription management */}
        <div style={{
          background: '#111113', border: `1px solid ${isSubscribed ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.06)'}`,
          borderRadius: 12, padding: '20px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#71717a', marginBottom: 6 }}>
              {isSubscribed ? 'Current Plan' : 'Free Plan'}
            </div>
            {isSubscribed && activePlan ? (
              <>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{activePlan.name} <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a' }}>{activePlan.price}</span></div>
                <div style={{ fontSize: 12, color: '#71717a', display: 'flex', gap: 16 }}>
                  <span>{activePlan.decisions}</span>
                  <span>·</span>
                  <span>{activePlan.traces}</span>
                  <span>·</span>
                  <span>{activePlan.retention}</span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, color: '#a1a1aa', marginBottom: 4 }}>
                5,000 decisions · 50K traces/mo · Free forever
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
            {isSubscribed ? (
              <button
                onClick={handleManageSubscription}
                disabled={portalMutation.isPending}
                style={{
                  padding: '9px 18px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.10)',
                  background: 'transparent', color: '#7a7a7a',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  opacity: portalMutation.isPending ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {portalMutation.isPending ? 'Opening...' : 'Manage subscription'}
              </button>
            ) : (
              <UpgradeButton />
            )}
          </div>
        </div>

        {/* Balance hero */}
        <div style={{
          background: '#111113', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16, padding: '28px 32px', marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 32,
        }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#71717a', marginBottom: 8 }}>
              Credit Balance
            </div>
            <div style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-2.5px', lineHeight: 1, color: '#22c55e', marginBottom: 8 }}>
              ${balance.toFixed(2)}
            </div>
            <div style={{ fontSize: 12, color: '#71717a' }}>Credits never expire · Auto-recharge off</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#71717a', lineHeight: 2 }}>
              <span style={{ color: '#a1a1aa' }}>$0.01</span> per routing decision<br />
              <span style={{ color: '#a1a1aa' }}>$0.001</span> per trace<br />
              Above free tier (5,000 decisions + 50K traces free/mo)
            </div>
          </div>
        </div>

        {/* Add credits */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#a1a1aa' }}>Add Credits</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 28 }}>
          {[10, 25, 50, 100].map(amount => (
            <button
              key={amount}
              onClick={() => handleAddCredits(amount)}
              disabled={creditsCheckoutMutation.isPending}
              style={{
                padding: '11px 22px', borderRadius: 10,
                border: `1px solid ${amount === 50 ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`,
                background: '#111113', color: '#fafafa',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                opacity: creditsCheckoutMutation.isPending ? 0.5 : 1,
              }}
            >
              ${amount}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#71717a', marginLeft: 4 }}>
            Powered by <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600,
              color: '#7a7a7a', background: 'rgba(255,255,255,0.06)',
              padding: '2px 7px', borderRadius: 4,
            }}>stripe</span> · Instant · Credits never expire
          </div>
        </div>

        {/* Free tier progress */}
        <div style={{
          background: '#111113', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12, padding: '20px 22px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Free tier — resets monthly</div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              padding: '2px 8px', borderRadius: 4,
              background: 'rgba(34,197,94,0.12)', color: '#22c55e', fontWeight: 500,
            }}>Always free</div>
          </div>
          <FreeRow label="Routing decisions" used={Math.min(decisionsUsed, decisionLimit)} total={decisionLimit} />
          <FreeRow label="Traces" used={Math.min(tracesUsed, traceLimit)} total={traceLimit} />
        </div>

        {/* Usage grid */}
        <div style={{
          fontSize: 13, fontWeight: 600, color: '#a1a1aa', marginBottom: 14,
          paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>This month — above free tier</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
          <UsageCard label="Routing Decisions" value={paidDecisions.toLocaleString()} sub={`$${(paidDecisions * 0.01).toFixed(2)} charged`} />
          <UsageCard label="Traces" value={paidTraces.toLocaleString()} sub={`$${(paidTraces * 0.001).toFixed(2)} charged`} />
          <UsageCard label="Total Spent" value={`$${totalSpent.toFixed(2)}`} sub="Deducted from balance" green />
          <UsageCard label="Est. End of Month" value={`$${estEndOfMonth.toFixed(2)}`} sub="At current pace" />
        </div>

        {/* Payment history */}
        <div style={{
          background: '#111113', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13, fontWeight: 600, color: '#a1a1aa' }}>
            Payment history
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Description', 'Type', 'Amount', 'Balance after', 'Receipt'].map((h, i) => (
                  <th key={h} style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: 'uppercase',
                    letterSpacing: '1px', color: '#71717a', textAlign: i >= 3 ? 'right' : 'left',
                    padding: '10px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(usageHistory && usageHistory.length > 0) ? (
                usageHistory.slice(0, 6).map((entry, i) => (
                  <HistoryRow
                    key={entry.month}
                    date={entry.month}
                    desc={`Usage — ${new Date(entry.month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}`}
                    type="usage"
                    amount={entry.cost_usd > 0 ? `−$${entry.cost_usd.toFixed(2)}` : '$0.00'}
                    amountColor={entry.cost_usd > 0 ? '#ef4444' : '#71717a'}
                    balanceAfter="—"
                    last={i === Math.min(usageHistory.length, 6) - 1}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ padding: '24px 18px', textAlign: 'center', color: '#555', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                    No usage history yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SidebarLayout>
  );
}

function FreeRow({ label, used, total }) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: '#71717a', width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 2, background: '#22c55e', width: `${pct}%` }} />
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#a1a1aa', whiteSpace: 'nowrap' }}>
        {used.toLocaleString()} / {total.toLocaleString()}
      </div>
    </div>
  );
}

function UsageCard({ label, value, sub, green }) {
  return (
    <div style={{
      background: '#111113',
      border: `1px solid ${green ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 10, padding: '16px 18px',
    }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '1.2px', color: '#71717a', marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 3,
        color: green ? '#22c55e' : '#fafafa',
      }}>{value}</div>
      <div style={{ fontSize: 11, color: '#71717a' }}>{sub}</div>
    </div>
  );
}

function HistoryRow({ date, desc, type, amount, amountColor, balanceAfter, receipt, last }) {
  const borderBottom = last ? 'none' : '1px solid rgba(255,255,255,0.06)';
  const tdBase = { padding: '11px 18px', borderBottom, fontSize: 13, verticalAlign: 'middle' };
  const tdNum = { ...tdBase, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' };
  return (
    <tr>
      <td style={{ ...tdBase, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#71717a' }}>{date}</td>
      <td style={tdBase}>{desc}</td>
      <td style={tdBase}>
        <span style={{
          display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
          fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500,
          background: type === 'credit' ? 'rgba(34,197,94,0.12)' : 'rgba(59,130,246,0.12)',
          color: type === 'credit' ? '#22c55e' : '#3b82f6',
        }}>{type}</span>
      </td>
      <td style={{ ...tdNum, color: amountColor }}>{amount}</td>
      <td style={tdNum}>{balanceAfter}</td>
      <td style={{ ...tdNum, color: '#71717a' }}>
        {receipt ? <span style={{ color: '#635bff', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>stripe ↗</span> : '—'}
      </td>
    </tr>
  );
}
